import moment from "moment";

let jsPDF;
try {
	jsPDF = require("jspdf").default || require("jspdf");
} catch (error) {
	console.error("jsPDF non è installato. Esegui: npm install jspdf");
	jsPDF = null;
}

/**
 * Genera un PDF con le informazioni dell'allenamento
 * @param {Object} trainingData - Dati dell'allenamento
 * @param {Array} teamPlayers - Lista dei giocatori della squadra
 * @param {Array} trainingExercises - Lista degli esercizi
 * @param {Function} t - Funzione di traduzione
 */
export const generateTrainingPDF = (
	trainingData,
	teamPlayers,
	trainingExercises,
	t,
) => {
	if (!jsPDF) {
		throw new Error(
			"jsPDF non è installato. Per utilizzare questa funzionalità, installa jsPDF eseguendo: npm install jspdf",
		);
	}

	// =========================
	// Document setup (A4, pt)
	// =========================
	const doc = new jsPDF({unit: "pt", format: "a4"});
	const pageWidth = doc.internal.pageSize.getWidth();
	const pageHeight = doc.internal.pageSize.getHeight();

	// =========================
	// Spacing system
	// =========================
	const SP = {
		pagePad: 48,
		blockGap: 14,
		cardPad: 14,
		footerH: 50,
	};

	let yPosition = SP.pagePad;

	// =========================
	// Theme
	// =========================
	const theme = {
		primary: [16, 89, 191],
		primaryDark: [9, 53, 128],
		primaryLight: [59, 130, 246],
		bg: [245, 247, 250],
		border: [229, 231, 235],
		text: [30, 41, 59],
		muted: [100, 116, 139],
		success: [34, 197, 94],
		warn: [251, 191, 36],
		danger: [239, 68, 68],
		yellow: [251, 191, 36],
		red: [239, 68, 68],
	};

	// =========================
	// Page break
	// =========================
	const checkPageBreak = (requiredHeight) => {
		if (yPosition + requiredHeight > pageHeight - SP.footerH) {
			doc.addPage();
			yPosition = SP.pagePad;
			return true;
		}
		return false;
	};

	// =========================
	// Helpers
	// =========================
	const safeText = (v, fallback = "---") =>
		v === null || v === undefined || v === "" ? fallback : String(v);

	const setOpacity = (opacity) => {
		try {
			if (doc.GState) doc.setGState(new doc.GState({opacity}));
		} catch (_) {
			/* no-op */
		}
	};

	const resetOpacity = () => {
		try {
			if (doc.GState) doc.setGState(new doc.GState({opacity: 1}));
		} catch (_) {
			/* no-op */
		}
	};

	const ellipsize = (
		text,
		maxWidth,
		fontSize = 11,
		font = ["helvetica", "normal"],
	) => {
		const s = safeText(text, "");
		if (!s) return "";
		doc.setFont(font[0], font[1]);
		doc.setFontSize(fontSize);
		if (doc.getTextWidth(s) <= maxWidth) return s;

		const ell = "…";
		let lo = 0;
		let hi = s.length;
		while (lo < hi) {
			const mid = Math.floor((lo + hi) / 2);
			const candidate = s.slice(0, mid) + ell;
			if (doc.getTextWidth(candidate) <= maxWidth) lo = mid + 1;
			else hi = mid;
		}
		return s.slice(0, Math.max(0, lo - 1)) + ell;
	};

	const wrapText = (
		text,
		maxWidth,
		fontSize = 11,
		font = ["helvetica", "normal"],
	) => {
		doc.setFont(font[0], font[1]);
		doc.setFontSize(fontSize);
		return doc.splitTextToSize(safeText(text, ""), maxWidth);
	};

	const drawCard = (
		x,
		y,
		w,
		h,
		{
			radius = 12,
			fill = [255, 255, 255],
			border = theme.border,
			shadow = true,
		} = {},
	) => {
		if (shadow) {
			doc.setFillColor(0, 0, 0);
			setOpacity(0.06);
			doc.roundedRect(x + 2, y + 3, w, h, radius, radius, "F");
			resetOpacity();
		}

		doc.setDrawColor(...border);
		doc.setLineWidth(0.8);
		doc.setFillColor(...fill);
		doc.roundedRect(x, y, w, h, radius, radius, "FD");
	};

	const drawSectionHeader = (title, {dotColor = theme.primary} = {}) => {
		const x = SP.pagePad;
		const w = pageWidth - SP.pagePad * 2;

		checkPageBreak(38);

		doc.setFillColor(...theme.primary);
		doc.roundedRect(x, yPosition, w, 22, 10, 10, "F");

		doc.setFillColor(...dotColor);
		doc.circle(x + 12, yPosition + 11, 4, "F");

		doc.setFont("helvetica", "bold");
		doc.setFontSize(12);
		doc.setTextColor(255, 255, 255);
		doc.text(title, x + 22, yPosition + 15);

		yPosition += 22 + 10;
		doc.setTextColor(...theme.text);
	};

	const drawKeyValueTable = (items) => {
		const x = SP.pagePad;
		const w = pageWidth - SP.pagePad * 2;

		const labelW = 150;
		const valueW = w - SP.cardPad * 2 - labelW - 10;

		const rows = (items || []).map((it) => {
			const lines = wrapText(it.value || "---", valueW, 11, [
				"helvetica",
				"normal",
			]);
			const rowH = Math.max(18, lines.length * 14);
			return {...it, lines, rowH};
		});

		const h = SP.cardPad * 2 + rows.reduce((sum, r) => sum + r.rowH, 0);

		checkPageBreak(h + 6);

		drawCard(x, yPosition, w, h, {radius: 12, shadow: true});

		let y = yPosition + SP.cardPad + 12;

		rows.forEach((r, idx) => {
			if (idx % 2 === 1) {
				doc.setFillColor(...theme.bg);
				doc.roundedRect(x + 8, y - 12, w - 16, r.rowH, 8, 8, "F");
			}

			doc.setFont("helvetica", "bold");
			doc.setFontSize(10);
			doc.setTextColor(...theme.muted);
			doc.text(`${safeText(r.label, "")}:`, x + SP.cardPad, y);

			doc.setFont("helvetica", "normal");
			doc.setFontSize(11);
			doc.setTextColor(...theme.text);
			doc.text(r.lines, x + SP.cardPad + labelW, y);

			y += r.rowH;
		});

		yPosition += h + SP.blockGap;
		doc.setTextColor(...theme.text);
	};

	const drawSubSectionLabel = (label) => {
		const x = SP.pagePad;
		const w = pageWidth - SP.pagePad * 2;

		checkPageBreak(18);

		doc.setFillColor(...theme.bg);
		doc.roundedRect(x, yPosition, w, 18, 9, 9, "F");

		doc.setFont("helvetica", "bold");
		doc.setFontSize(10);
		doc.setTextColor(...theme.primaryDark);
		doc.text(label, x + 10, yPosition + 13);

		yPosition += 18 + 8;
		doc.setTextColor(...theme.text);
	};

	const drawListCard = ({
		title,
		items,
		dotColor = theme.primary,
		itemRenderer,
		countBadge = true,
		rowHeight = 18,
	}) => {
		if (!items || items.length === 0) return;

		const x = SP.pagePad;
		const w = pageWidth - SP.pagePad * 2;

		const headerH = 22;
		const headerGap = 10;

		const availableH =
			pageHeight -
			SP.footerH -
			SP.pagePad -
			(headerH + headerGap) -
			SP.cardPad * 2;
		const maxRowsPerPage = Math.max(1, Math.floor(availableH / rowHeight));

		let idx = 0;

		while (idx < items.length) {
			const rowsThisPage = Math.min(maxRowsPerPage, items.length - idx);

			const cardH = SP.cardPad * 2 + rowsThisPage * rowHeight;

			checkPageBreak(headerH + headerGap + cardH + SP.blockGap);

			// Header
			doc.setFillColor(...theme.primary);
			doc.roundedRect(x, yPosition, w, headerH, 10, 10, "F");

			doc.setFillColor(...dotColor);
			doc.circle(x + 12, yPosition + 11, 4, "F");

			doc.setFont("helvetica", "bold");
			doc.setFontSize(12);
			doc.setTextColor(255, 255, 255);

			const headerText = countBadge ? `${title} (${items.length})` : title;
			doc.text(headerText, x + 22, yPosition + 15);

			yPosition += headerH + headerGap;

			// Card
			drawCard(x, yPosition, w, cardH, {radius: 12, shadow: true});

			let y = yPosition + SP.cardPad + 12;

			for (let r = 0; r < rowsThisPage; r++) {
				const item = items[idx + r];

				if (r % 2 === 1) {
					doc.setFillColor(...theme.bg);
					doc.roundedRect(x + 8, y - 12, w - 16, rowHeight, 8, 8, "F");
				}

				if (itemRenderer) itemRenderer({item, x, y, w, rowHeight});
				else {
					doc.setFillColor(...dotColor);
					doc.circle(x + 12, y - 2, 3, "F");

					doc.setFont("helvetica", "normal");
					doc.setFontSize(11);
					doc.setTextColor(...theme.text);
					const name =
						`${safeText(item?.firstName, "")} ${safeText(item?.lastName, "")}`.trim();
					doc.text(
						ellipsize(name, w - 60, 11, ["helvetica", "normal"]),
						x + 24,
						y,
					);
				}

				y += rowHeight;
			}

			yPosition += cardH + SP.blockGap;

			idx += rowsThisPage;
			doc.setTextColor(...theme.text);
		}
	};

	// =========================
	// HEADER (premium)
	// =========================
	const headerH = 92;

	doc.setFillColor(...theme.primaryDark);
	doc.rect(0, 0, pageWidth, headerH, "F");

	doc.setFillColor(...theme.primary);
	doc.rect(0, headerH - 20, pageWidth, 20, "F");

	doc.setFillColor(...theme.primaryLight);
	doc.rect(0, headerH - 3, pageWidth, 3, "F");

	doc.setFont("helvetica", "bold");
	doc.setFontSize(22);
	doc.setTextColor(255, 255, 255);
	doc.text("REPORT ALLENAMENTO", SP.pagePad, 46);

	const dateText = trainingData?.startDate
		? moment(trainingData.startDate).format("DD MMMM YYYY")
		: "";
	const catText = safeText(trainingData?.playersCategory, "");
	const subtitle = [dateText, catText].filter(Boolean).join(" • ");

	doc.setFont("helvetica", "normal");
	doc.setFontSize(10);
	doc.setTextColor(235, 245, 255);
	doc.text(subtitle, SP.pagePad, 66);

	// watermark soft
	setOpacity(0.06);
	doc.setFont("helvetica", "bold");
	doc.setFontSize(44);
	doc.setTextColor(255, 255, 255);
	doc.text("TRAINING", pageWidth - SP.pagePad, 56, {align: "right"});
	resetOpacity();

	yPosition = headerH + 22;
	doc.setTextColor(...theme.text);

	// =========================
	// HERO INFO CARD (Data / Ora / Campo)
	// =========================
	{
		const x = SP.pagePad;
		const w = pageWidth - SP.pagePad * 2;
		const h = 76;

		checkPageBreak(h + SP.blockGap);
		drawCard(x, yPosition, w, h, {radius: 14, shadow: true});

		doc.setFont("helvetica", "bold");
		doc.setFontSize(9);
		doc.setTextColor(...theme.muted);
		doc.text("SINTESI", x + SP.cardPad, yPosition + 18);

		const colW = w - SP.cardPad * 2;
		const col = Math.floor(colW / 3);

		const start = trainingData?.startDate
			? moment(trainingData.startDate)
			: null;
		const end = trainingData?.endDate ? moment(trainingData.endDate) : null;

		const vDate = start ? start.format("DD/MM/YYYY") : "---";
		const vTime = start
			? start.format("HH:mm") + (end ? ` - ${end.format("HH:mm")}` : "")
			: "---";
		const vField = safeText(trainingData?.fieldName);

		const baseY = yPosition + 48;

		// labels
		doc.setFont("helvetica", "bold");
		doc.setFontSize(9);
		doc.setTextColor(...theme.muted);
		doc.text("Data", x + SP.cardPad, yPosition + 34);
		doc.text("Orario", x + SP.cardPad + col, yPosition + 34);
		doc.text("Campo", x + SP.cardPad + col * 2, yPosition + 34);

		// values
		doc.setFont("helvetica", "bold");
		doc.setFontSize(14);
		doc.setTextColor(...theme.text);
		doc.text(vDate, x + SP.cardPad, baseY);

		doc.text(vTime, x + SP.cardPad + col, baseY);

		doc.text(
			ellipsize(vField, col - 10, 14, ["helvetica", "bold"]),
			x + SP.cardPad + col * 2,
			baseY,
		);

		// dividers
		doc.setDrawColor(...theme.border);
		doc.setLineWidth(1);
		doc.line(
			x + SP.cardPad + col - 10,
			yPosition + 26,
			x + SP.cardPad + col - 10,
			yPosition + h - 14,
		);
		doc.line(
			x + SP.cardPad + col * 2 - 10,
			yPosition + 26,
			x + SP.cardPad + col * 2 - 10,
			yPosition + h - 14,
		);

		yPosition += h + SP.blockGap;
	}

	// =========================
	// INFO PRINCIPALI
	// =========================
	drawSectionHeader("Informazioni Principali");

	const mainInfo = [
		{
			label: t?.("date_label") || "Data",
			value: trainingData?.startDate
				? moment(trainingData.startDate).format("DD/MM/YYYY")
				: "---",
		},
		{
			label: t?.("time_label") || "Orario",
			value: trainingData?.startDate
				? moment(trainingData.startDate).format("HH:mm") +
					(trainingData?.endDate
						? " - " + moment(trainingData.endDate).format("HH:mm")
						: "")
				: "---",
		},
		{
			label: t?.("category") || "Categoria",
			value: safeText(trainingData?.playersCategory),
		},
		{
			label: "Campo",
			value: safeText(trainingData?.fieldName),
		},
	];

	drawKeyValueTable(mainInfo);

	// =========================
	// PLAYERS (Presenti / Assenti / Infortunati)
	// =========================
	const convocations = trainingData?.trainingConvocations || [];
	const players = teamPlayers || [];

	const resolvePlayer = (playerId) => players.find((p) => p?.id === playerId);
	const sortByLastName = (a, b) =>
		(a?.lastName || "").localeCompare(b?.lastName || "");

	const presents = convocations
		.filter((c) => c?.status === "Presente")
		.map((c) => resolvePlayer(c?.playerId))
		.filter(Boolean)
		.sort(sortByLastName);

	const absents = convocations
		.filter((c) => c?.status === "Assente")
		.map((c) => resolvePlayer(c?.playerId))
		.filter(Boolean)
		.sort(sortByLastName);

	const injured = convocations
		.filter((c) => c?.status === "Infortunato")
		.map((c) => resolvePlayer(c?.playerId))
		.filter(Boolean)
		.sort(sortByLastName);

	drawListCard({
		title: "Presenti",
		items: presents,
		dotColor: theme.success,
		rowHeight: 18,
	});

	drawListCard({
		title: "Assenti",
		items: absents,
		dotColor: theme.muted,
		rowHeight: 18,
	});

	drawListCard({
		title: "Infortunati",
		items: injured,
		dotColor: theme.warn,
		rowHeight: 18,
	});

	// =========================
	// ESERCIZI
	// =========================
	if (trainingExercises && trainingExercises.length > 0) {
		drawSectionHeader(`Esercizi Svolti (${trainingExercises.length})`, {
			dotColor: theme.primaryLight,
		});

		const phasesOptions = [
			{value: 0, label: "Fase 0"},
			{value: 1, label: "Momento 1"},
			{value: 2, label: "Momento 2"},
			{value: 3, label: "Momento 3"},
			{value: 4, label: "Momento 4"},
		];

		const exercisesByPhase = trainingExercises.reduce((acc, ex) => {
			const phase = ex?.phase ?? "0";
			if (!acc[phase]) acc[phase] = [];
			acc[phase].push(ex);
			return acc;
		}, {});

		const phaseKeys = Object.keys(exercisesByPhase).sort(
			(a, b) => parseInt(a) - parseInt(b),
		);

		const renderExerciseCard = (exercise, phaseExercises) => {
			const x = SP.pagePad;
			const w = pageWidth - SP.pagePad * 2;

			const name = safeText(exercise?.exercise?.name, "Esercizio senza nome");
			const details = [];
			if (exercise?.exercise?.exerciseType)
				details.push(`Tipo: ${exercise.exercise.exerciseType}`);
			if (exercise?.duration) details.push(`Durata: ${exercise.duration}'`);

			const desc = safeText(exercise?.description, "");
			const nameLines = wrapText(name, w - SP.cardPad * 2 - 26, 12, [
				"helvetica",
				"bold",
			]);
			const detailsLines =
				details.length > 0
					? wrapText(details.join(" • "), w - SP.cardPad * 2 - 26, 10, [
							"helvetica",
							"normal",
						])
					: [];
			const descLines = desc
				? wrapText(desc, w - SP.cardPad * 2 - 26, 10, ["helvetica", "normal"])
				: [];

			// compute height
			const lineH = 14;
			let h =
				SP.cardPad * 2 +
				nameLines.length * lineH +
				(detailsLines.length ? detailsLines.length * 12 + 6 : 0) +
				(descLines.length ? descLines.length * 12 + 8 : 0);

			h = Math.max(h, 64);

			checkPageBreak(h + 8);

			drawCard(x, yPosition, w, h, {radius: 12, shadow: true});

			// left badge number
			const exNum = exercise?.sorting || phaseExercises.indexOf(exercise) + 1;

			doc.setFillColor(...theme.primary);
			doc.roundedRect(x + 12, yPosition + 14, 26, 26, 8, 8, "F");
			doc.setFont("helvetica", "bold");
			doc.setFontSize(12);
			doc.setTextColor(255, 255, 255);
			doc.text(String(exNum), x + 25, yPosition + 32, {align: "center"});

			const textX = x + 12 + 26 + 12;
			let cursorY = yPosition + 26;

			// Name
			doc.setFont("helvetica", "bold");
			doc.setFontSize(12);
			doc.setTextColor(...theme.text);
			doc.text(nameLines, textX, cursorY);
			cursorY += nameLines.length * lineH;

			// Details
			if (detailsLines.length > 0) {
				cursorY += 4;
				doc.setFont("helvetica", "normal");
				doc.setFontSize(10);
				doc.setTextColor(...theme.muted);
				doc.text(detailsLines, textX, cursorY);
				cursorY += detailsLines.length * 12;
			}

			// Description
			if (descLines.length > 0) {
				cursorY += 8;
				doc.setFont("helvetica", "normal");
				doc.setFontSize(10);
				doc.setTextColor(...theme.text);
				doc.text(descLines, textX, cursorY);
			}

			yPosition += h + 10;
		};

		phaseKeys.forEach((phase) => {
			const phaseExercises = (exercisesByPhase[phase] || []).sort(
				(a, b) => (a?.sorting || 0) - (b?.sorting || 0),
			);

			const phaseLabel =
				phasesOptions.find((p) => p.value === parseInt(phase))?.label ||
				`Fase ${phase}`;

			drawSubSectionLabel(`${phaseLabel} (${phaseExercises.length})`);

			phaseExercises.forEach((ex) => renderExerciseCard(ex, phaseExercises));

			yPosition += 6;
		});
	}

	// =========================
	// FOOTER (clean)
	// =========================
	const totalPages = doc.internal.pages.length - 1;
	const generatedAt = `Generato il ${moment().format("DD/MM/YYYY • HH:mm")}`;

	for (let i = 1; i <= totalPages; i++) {
		doc.setPage(i);

		doc.setDrawColor(...theme.border);
		doc.setLineWidth(1);
		doc.line(
			SP.pagePad,
			pageHeight - SP.footerH + 18,
			pageWidth - SP.pagePad,
			pageHeight - SP.footerH + 18,
		);

		doc.setFont("helvetica", "normal");
		doc.setFontSize(9);
		doc.setTextColor(...theme.muted);
		doc.text(generatedAt, SP.pagePad, pageHeight - 18);

		doc.text(
			`Pagina ${i} / ${totalPages}`,
			pageWidth - SP.pagePad,
			pageHeight - 18,
			{
				align: "right",
			},
		);
	}

	// =========================
	// Save
	// =========================
	const datePart = trainingData?.startDate
		? moment(trainingData.startDate).format("DD-MM-YYYY")
		: "N-A";

	const catSafe = safeText(trainingData?.playersCategory, "N-A").replace(
		/[\\/:*?"<>|]/g,
		"-",
	);

	const fileName = `Allenamento_${datePart}_${catSafe}.pdf`;
	doc.save(fileName);
};
